import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { aiAgentsData } from '@/mocks/homepageData';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const riskColorMap: Record<string, string> = {
  'neon-green': 'text-[#00ff88] border-[#00ff88]/30 bg-[#00ff88]/10',
  warning: 'text-[#ffaa00] border-[#ffaa00]/30 bg-[#ffaa00]/10',
  danger: 'text-[#ff4444] border-[#ff4444]/30 bg-[#ff4444]/10',
};

const riskKeyMap: Record<string, string> = {
  'High': 'high',
  'Low': 'low',
  'Very High': 'veryHigh',
  'Medium': 'medium',
};

export default function AIAgentsSection() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const bgWhite5 = isDark ? 'bg-white/5' : 'bg-black/5';
  const bgWhite10 = isDark ? 'bg-white/10' : 'bg-black/10';
  const borderWhite10 = isDark ? 'border-white/10' : 'border-black/10';
  const hoverBgWhite5 = isDark ? 'hover:bg-white/5' : 'hover:bg-black/5';
  const hoverTextWhite = isDark ? 'hover:text-white' : 'hover:text-[#111]';

  return (
    <section
      id="ai-agents"
      ref={sectionRef}
      className={`relative w-full py-16 md:py-24 border-t ${borderLight}`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="text-xs text-[#00ff88] font-mono uppercase tracking-widest">{t('agents.autonomousIntelligence')}</span>
          <h2 className={`mt-3 text-2xl md:text-3xl lg:text-4xl font-bold ${textPrimary}`}>
            {t('agents.aiTradingAgents')}
          </h2>
          <p className={`mt-3 text-sm md:text-base max-w-2xl mx-auto ${textSecondary}`}>
            {t('agents.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {aiAgentsData.map((agent, index) => (
            <motion.div
              key={agent.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass-card-hover p-5 relative overflow-hidden group"
            >
              {/* Scanning effect */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                  initial={{ top: '-100%' }}
                  animate={{ top: '200%' }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'linear',
                    delay: index * 0.3,
                  }}
                  className="absolute left-0 right-0 h-8 bg-gradient-to-b from-transparent via-[#00ff88]/10 to-transparent"
                />
              </div>

              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bgWhite5}`}>
                  <i className="ri-robot-2-line w-5 h-5 flex items-center justify-center text-[#00ff88]" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="status-dot live" />
                  <span className="text-[10px] text-[#00ff88] font-mono uppercase">{t('hero.live')}</span>
                </div>
              </div>

              {/* Name & Description */}
              <h3 className={`text-base font-semibold mb-1.5 ${textPrimary}`}>{t(`content.agents.${agent.id}.name`, agent.name)}</h3>
              <p className={`text-xs leading-relaxed mb-4 line-clamp-2 ${textMuted}`}>
                {t(`content.agents.${agent.id}.description`, agent.description)}
              </p>

              {/* Stats */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${textMuted}`}>{t('agents.pnl30d')}</span>
                  <span className="text-sm font-mono font-semibold text-[#00ff88]">+{agent.pnl}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${textMuted}`}>{t('agents.winRate')}</span>
                  <span className={`text-sm font-mono ${textPrimary}`}>{agent.winRate}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${textMuted}`}>{t('agents.trades24h')}</span>
                  <span className={`text-sm font-mono ${textPrimary}`}>{agent.trades24h.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${textMuted}`}>{t('agents.avgHold')}</span>
                  <span className={`text-sm font-mono ${textPrimary}`}>{agent.avgHoldTime}</span>
                </div>
              </div>

              {/* Risk badge */}
              <div className={`mt-4 pt-3 border-t ${borderLight}`}>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider border ${
                    riskColorMap[agent.riskColor] || riskColorMap.warning
                  }`}
                >
                  {t(`content.riskLevels.${riskKeyMap[agent.riskLevel] || 'medium'}`)} {t('agents.risk')}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-10"
        >
          <a
            href="#"
            className={`inline-flex items-center gap-2 px-6 py-3 border text-sm font-medium rounded-lg transition-all ${bgWhite5} ${borderWhite10} ${textPrimary} ${bgWhite10} ${hoverTextWhite}`}
          >
            {t('agents.exploreAll')}
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}